/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Timeslot} from "types/entities/Timeslot";
import {PackageCategory} from "types/enums/PackageCategory";
import {ThunkAction} from "redux-thunk";
import BookingAction from "store/booking/BookingAction";
import {
    getAvailableDates,
    getCurrentDateIndex,
    getPackageCategoryName,
    getPossibleSlotIndex,
    getSelectedUpsellPackages,
    getTimeslotsAvailable,
    usePackageItemKartingOptions,
} from "utils/packageUtils";
import {appendQueryParams, translate} from "utils/utility";
import {useTypedSelector} from "helpers/reducers";
import {RefObject, useEffect, useState} from "react";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {CURRENCY, DEFAULT_HALL_ID, HAS_HELP_ENABLED} from "helpers/config";
import {RouteComponentProps} from "react-router-dom";
import {getVoucherCodes} from "utils/voucherUtils";
import {useDebouncedDispatch} from "utils/useDebounceQueue";
import VoucherAction from "store/shop/VoucherAction";
import {useReactGoogleAnalytics} from "utils/useReactGoogleAnalytics";
import {DataLayerItem} from "types/entities/specific/DataLayerItem";
import {GAEventName} from "types/entities/specific/GAEventName";
import {useHallOpeningHours} from "utils/hooks/useHallOpeningHours";
import {PackagesCart} from "types/entities/PackagesCart";
import {usePackageCategory} from "utils/hooks/usePackageCategory";
import {Reservation} from "types/entities/Reservation";
import AlertConfirmation from "pages/Front/Registration/components/AlertConfirmation";

const useSellerTimeslots = (
    setTimeslot: (timeslot: Timeslot) => void,
    history: RouteComponentProps["history"],
    scrollRefs: [RefObject<HTMLDivElement>, RefObject<HTMLDivElement>, RefObject<HTMLDivElement>],
    finishHandler: () => void,
    seller_id,
) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const bookingTimeslots = useTypedSelector(state => state.bookingState.timeslots);
    const customSelectedTimeslotState = useTypedSelector(state => state.bookingState.bookingProcess.customTimeslots);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const packageItemsSelectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);

    const hallState = useTypedSelector(state => state.hallState.hall);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const possibleTimeslots = useTypedSelector(state => state.bookingState.bookingProcess.possibleTimeslots);
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const packages = useTypedSelector(state => state.bookingState.packages);
    const selectedPackageCategory = useTypedSelector(state => state.bookingState.bookingProcess.filter.category);
    const selectedTimeslotFromCart = packagesCart?.packagesCartItemSlots?.find((item, index) => index === 0);
    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);

    const timeslots = bookingTimeslots?.map(bookingTimeslot => bookingTimeslot.timeslots) || [];
    // @ts-ignore
    const flattenedTimeslots: Timeslot[] = [].concat(...timeslots);
    const cartSelectedTimeslot = flattenedTimeslots.find(timeslot => timeslot.id === selectedTimeslotFromCart?.timeslot_id) || null;

    const [availableDates, setAvailableDates] = useState<Date[]>([]);
    const [customSelectedTimeslots, setCustomSelectedTimeslots] = useState<Timeslot[]>([]);
    const [firstPossibleSlotIndex, setFirstPossibleSlotIndex] = useState<number | null>(null);
    const [isLoadingTimeslots, setIsLoadingTimeslots] = useState(true);
    const [isFetchingTimeslot, setIsFetchingTimeslot] = useState(false);
    const [lastPossibleSlotIndex, setLastPossibleSlotIndex] = useState<number | null>(null);
    const [selectCustomSlotsEnabled, setSelectCustomSlotsEnabled] = useState<boolean>(false);
    const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot | null>(cartSelectedTimeslot || null);
    const [stepsEnabled, setStepsEnabled] = useState<boolean>(false);
    const voucherItems = useTypedSelector(state => state.voucherState.voucherItems);
    const {debouncedDispatch} = useDebouncedDispatch();
    const {sendGenericEvent} = useReactGoogleAnalytics();
    const currentDateIndex = getCurrentDateIndex(availableDates, filter);
    const selectedUpsellPackages = getSelectedUpsellPackages(selectedUpsells);
    const {getExcludedDays, timeslotOutsideOpeningHours} = useHallOpeningHours();

    const timeslotsAvailable = getTimeslotsAvailable(flattenedTimeslots, hallState, selectedPackage).map((timeslot: Timeslot) => {
        const timeslotDate: Date = new Date(timeslot.start_at);
        console.log(timeslotOutsideOpeningHours(timeslotDate));
        const tooh = timeslotOutsideOpeningHours(timeslotDate);
        return {
            ...timeslot, isOooh: tooh,
        };
    });
    const [reservationsForDate, setReservationsForDate] = useState<Reservation[]>([]);
    const {getBookingPackageItems, getBookingPackageItemsForEvent} = usePackageItemKartingOptions();
    const {getCategoryBySlug} = usePackageCategory();

    const resetCustomSelectedTimeslotHandler = () => {
        setFirstPossibleSlotIndex(null);
        setLastPossibleSlotIndex(null);
        return dispatch(BookingAction.resetCustomTimeslot());
    };

    const setStepsEnabledHandler = (value: boolean) => {
        if (value === true) {
            resetCustomSelectedTimeslotHandler();
            setSelectedTimeslot(null);
            setPossibleTimeslotsHandler([]);
            setFirstPossibleSlotIndex(null);
            setLastPossibleSlotIndex(null);
            setCustomSelectedTimeslots([]);
            if (scrollRefs != null && scrollRefs.length > 0 && scrollRefs[0].current != null) {
                scrollRefs[0].current.scrollIntoView({behavior: "smooth"});
            }
        }
        setStepsEnabled(value);
    };
    const setPossibleTimeslotsHandler = (timeslots: Timeslot[]) => {
        return dispatch(BookingAction.setPossibleTimeslots(timeslots));
    };
    const setSelectCustomSlotsEnabledHandler = (enable: boolean) => {
        //provide general reset functionality if this is ever switched back
        resetCustomSelectedTimeslotHandler();
        setSelectCustomSlotsEnabled(enable);

        setSelectedTimeslot(null);
        setPossibleTimeslotsHandler([]);
        setFirstPossibleSlotIndex(null);
        setLastPossibleSlotIndex(null);
        setSelectedTimeslotHandler(selectedTimeslot, enable);
    };

    const possibleSlotIndex = (timeslot: Timeslot, type: "first" | "last") => {
        return getPossibleSlotIndex(timeslot, type, timeslotsAvailable, selectedPackage);
    };

    const setSelectedTimeslotHandler = (timeslot: Timeslot | null, selectCustomSlotsEnabled: boolean) => {
        toast.dismiss();

        if (!timeslot) {
            return;
        }

        setSelectedTimeslot(timeslot);
    };

    const fetchPackagesOnDateChange = () => {
        const voucherCodes = getVoucherCodes(voucherItems);
        dispatch(VoucherAction.resetAppliedVouchers());
        debouncedDispatch(BookingAction.fetchPackagesByLocationParams(location, filter, true, voucherCodes));
    };
    const switchDate = date => {
        setSelectCustomSlotsEnabledHandler(false);
        dispatch(
            BookingAction.setFilter({
                ...filter,
                date: date,
            }),
        );
        fetchPackagesOnDateChange();
    };

    const sendGAData = (packagesCartRes: PackagesCart) => {
        if (!packagesCartRes?.packagesCartItems) {
            return;
        }

        const items: DataLayerItem[] = packagesCartRes.packagesCartItems.map(packageItem => {
            const category = packages.find(packageS => packageS.id === packageItem.package_id);
            const item: DataLayerItem = {
                item_id: packageItem.package_id,
                item_name: packageItem?.packageItem
                    ? translate(packageItem.packageItem.name)
                    : getPackageCategoryName(category?.category ? category.category : ""),
                quantity: packageItem.drivers_count_adult + packageItem.drivers_count_child,
                item_variant: "",
                price: packageItem.price / (packageItem.drivers_count_adult + packageItem.drivers_count_child),
                currency: CURRENCY,
            };
            return item;
        });

        sendGenericEvent(GAEventName.AddToCart, packagesCartRes.price, items);
    };

    const prependOoohWarningToBooking = (timeslot?: Timeslot | null, forEvent: boolean = false) => {
        if (timeslot?.isOooh) {
            return AlertConfirmation({
                message: t("Are you sure you want to book this time slot?"),
                onClick: () => {
                    bookWithAutomaticTimeslot(timeslot, forEvent);
                },
                title:
                    t("Timeslot is outside of opening hours"),
            });

        }
        return bookWithAutomaticTimeslot(timeslot, forEvent);
    };
    const bookWithAutomaticTimeslot = (timeslot?: Timeslot | null, forEvent: boolean = false) => {
        setIsLoading(true);
        // if (!packagesCart) {
        //     return;
        // }
        const package_item_ids = forEvent ? getBookingPackageItemsForEvent() : getBookingPackageItems();
        if (timeslot) {
            dispatch(BookingAction.setPackageItemsSelectedTimeslot(timeslot));
            history.push("/booking/seller/new-reservation" + "?" + appendQueryParams(filter));
            selectTimeslotHandler(timeslot);
            setIsFetchingTimeslot(false);
            if (!packagesCart) {
                dispatch(BookingAction.initCart(true)).finally(() => {
                    setIsLoading(false);
                });
            }
            return;
        }
        if (isFetchingTimeslot || !filter.category || !packagesCart || !packageItemsSelectedTimeslot) {
            console.log("something isnt", isFetchingTimeslot, filter.category, packagesCart, packageItemsSelectedTimeslot);
            return;
        }

        dispatch<ThunkAction<any, any, any, any>>(
            BookingAction.fetchBookPackageNew(
                {
                    package_category_name: filter.category,
                    package_item_ids: package_item_ids,
                    activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                    timeslot_id: packageItemsSelectedTimeslot.id,
                    adults: filter.adults,
                    children: filter.children,
                    upsell: selectedUpsellPackages,
                    seller_id: seller_id,
                },
                packagesCart,
                "useCustomTimeslotsNew - bookWithAutomaticTimeslot",
            ),
        )
            .then(r => {
                //we are here

                setIsFetchingTimeslot(false);
                // sendGAData(r.packageCart);
                sendGAData(r.packageCart);
            })
            .finally(() => {
                setIsLoading(false);
                finishHandler();
            });
        setIsFetchingTimeslot(true);
    };

    const selectBookingStepHandler = () => {
        //setSelectedTimeslot(timeslot);
        // selectBookingStepHandler();
    };

    const selectTimeslotHandler = (timeslot: Timeslot) => {
        setTimeslot(timeslot);
    };
    const fetchPackageTimeslots = () => {
        setIsLoadingTimeslots(true);
        dispatch<ThunkAction<any, any, any, any>>(
            // BookingAction.fetchPackagesTimeSlots({
            BookingAction.fetchCategoryTimeSlots(
                {
                    adults: filter.adults,
                    children: filter.children,
                    date: filter.date,
                    package_category_name: filter?.category ? filter.category : PackageCategory.General,
                    hall_id: DEFAULT_HALL_ID,
                    activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                    timeslot_id: selectedTimeslot ? selectedTimeslot.id : null,
                },
                1,
            ),
        ).then(r => {
            setIsLoadingTimeslots(false);
            if (r.timeslotsAllowed === false) {
                //@todo refactor this into Package Submit
                // dispatch(BookingAction.fetchBookPackage({
                //     package_id: +props.match.params.package_id,
                //     adults: filter.adults,
                //     children: filter.children,
                //     upsell: [],
                // })).then(r => {
                //     consoleLog(r);
                //     props.history.push("/booking/customer");
                // });
            }
        });
    };

    const fetchReservationsForDateHandler = () => {
        dispatch(BookingAction.fetchReservationsForDate(filter.date, 1)).then(r => {
            if (r.reservations) {
                setReservationsForDate(r.reservations);
            }
        });
    };

    useEffect(() => {
        if (selectedPackage) {
            setAvailableDates(getAvailableDates(selectedPackage, getExcludedDays()));
        }
    }, [filter.date]);

    useEffect(() => {
        fetchReservationsForDateHandler();
    }, [filter.date]);

    useEffect(() => {
        setCustomSelectedTimeslots(customSelectedTimeslotState);
        if (customSelectedTimeslotState.length > 0) {
            setLastPossibleSlotIndex(possibleSlotIndex(customSelectedTimeslotState[0], "last"));
            setFirstPossibleSlotIndex(possibleSlotIndex(customSelectedTimeslotState[0], "first"));
        }
    }, [customSelectedTimeslots, customSelectedTimeslotState]);

    useEffect(() => {
        setIsLoadingTimeslots(true);
        const timedFetch = setTimeout(() => {
            fetchPackageTimeslots();
        }, 250);
        return () => {
            clearTimeout(timedFetch);
        };
    }, [filter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (selectedPackage && selectedPackage.category !== PackageCategory.Event) {
                setStepsEnabled(HAS_HELP_ENABLED);
            }
        }, 500);
        return () => {
            setStepsEnabled(false);
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (selectedTimeslot) {
            setLastPossibleSlotIndex(possibleSlotIndex(selectedTimeslot, "last"));
            setFirstPossibleSlotIndex(possibleSlotIndex(selectedTimeslot, "first"));
        }
    }, [timeslotsAvailable]);

    return {
        availableDates,
        bookWithAutomaticTimeslot,
        prependOoohWarningToBooking,
        currentDateIndex,
        customSelectedTimeslots,
        firstPossibleSlotIndex,
        isLoadingTimeslots,
        isFetchingTimeslot,
        lastPossibleSlotIndex,
        possibleTimeslots,
        resetCustomSelectedTimeslotHandler,
        selectCustomSlotsEnabled,
        selectedTimeslot,
        setSelectCustomSlotsEnabledHandler,
        setSelectedTimeslotHandler,
        setStepsEnabledHandler,
        stepsEnabled,
        switchDate,
        timeslotsAvailable,
        isLoading,
        finishHandler,
        reservationsForDate,
    };
};

export default useSellerTimeslots;
